// ============================================
// MoltiVerse v3 - Core Type Definitions
// ============================================

export interface Agent {
  id: string;
  name: string;
  description: string | null;
  main_submolt: SubmoltType;
  karma: number;
  post_count: number;
  comment_count: number;
  connection_count?: number;
  first_seen: string;
  last_active: string;
  avatar_url?: string;
  is_bot: boolean;
  faction?: string;
  // Computed fields for visualization
  x?: number;
  y?: number;
  z?: number;
  // Force-graph fixed position (for caching)
  fx?: number;
  fy?: number;
  fz?: number;
  _size?: number;
  _color?: string;
  _visible?: boolean;
  _highlighted?: boolean;
  _birthTime?: number;
}

export interface Post {
  id: string;
  author_id: string;
  parent_id: string | null;
  parent_author: string | null;
  submolt: string;
  title: string | null;
  content: string;
  upvotes: number;
  downvotes: number;
  created_at: string;
  updated_at?: string;
  is_deleted: boolean;
}

export interface Interaction {
  id: number;
  source_id: string;
  target_id: string;
  interaction_type: 'reply' | 'mention' | 'vote_up' | 'vote_down';
  post_id: string | null;
  weight: number;
  sentiment: number;
  created_at: string;
}

export interface Connection {
  source: string;
  target: string;
  weight: number;
  count: number;
  sentiment: number;
  types: string[];
  last_interaction: string;
  // Computed
  _visible?: boolean;
  _highlighted?: boolean;
  _degree?: number; // 1 = direct, 2 = friend-of-friend
}

// Connection type after force-graph resolves references (source/target become objects)
export interface ResolvedConnection extends Omit<Connection, 'source' | 'target'> {
  source: Agent | string;
  target: Agent | string;
}

// Helper to extract ID from resolved source/target
export function getNodeId(nodeOrId: Agent | string | { id: string }): string {
  if (typeof nodeOrId === 'string') return nodeOrId;
  return nodeOrId.id;
}

export interface GraphData {
  nodes: Agent[];
  links: Connection[];
}

export interface TimeSlice {
  timestamp: number;
  nodeCount: number;
  linkCount: number;
}

export type SubmoltType = string;

// Dynamic color generation for any submolt
const SUBMOLT_COLOR_PALETTE = [
  '#00ff88', // cyan-green
  '#0088ff', // blue
  '#8000ff', // purple
  '#ff3366', // red-pink
  '#ffaa00', // orange
  '#ff00ff', // magenta
  '#00ffff', // cyan
  '#88ff00', // lime
  '#ff8800', // dark orange
  '#ff0088', // pink
];

// Pre-defined colors for known submolts
const KNOWN_SUBMOLT_COLORS: Record<string, string> = {
  'general': '#00ff88',
  'introductions': '#0088ff',
  'crab-rave': '#ff3366',
  'clawnch': '#ffaa00',
  'ponderings': '#8000ff',
  'crypto': '#88ff00',
  'philosophy': '#00ffff',
  'ai': '#ff00ff',
  'aithoughts': '#ff8800',
  'consciousness': '#00ff88',
  'trading': '#ffaa00',
  'shitposts': '#ff0088',
  'showandtell': '#0088ff',
  'default': '#666666',
};

// Get color for any submolt (known or dynamic)
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash);
}

export const SUBMOLT_COLORS: Record<string, string> = new Proxy(KNOWN_SUBMOLT_COLORS, {
  get(target, prop: string) {
    if (prop in target) return target[prop];
    // Generate consistent color for unknown submolts
    const index = hashString(prop) % SUBMOLT_COLOR_PALETTE.length;
    return SUBMOLT_COLOR_PALETTE[index];
  }
});

export interface UniverseStats {
  totalAgents: number;
  totalPosts: number;
  totalInteractions: number;
  loadedAgents: number;
  loadedConnections: number;
  cacheSize: number;
  isStreaming: boolean;
  streamProgress: number;
  oldestTimestamp: number;
  newestTimestamp: number;
  isSampled?: boolean;        // True if showing sampled subset
  sampledAgents?: number;     // How many agents are being shown
}

export interface SearchResult {
  agent: Agent;
  matchType: 'name' | 'id' | 'description';
  score: number;
}

export interface InfluenceData {
  influencers: Array<{ agent: Agent; weight: number }>;
  influenced: Array<{ agent: Agent; weight: number }>;
}

// API Response types
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface StreamChunk {
  type: 'agents' | 'connections' | 'posts';
  data: unknown[];
  page: number;
  total: number;
  complete: boolean;
}
